import { Component, OnInit, Inject, ChangeDetectorRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material';
import { ToastrService } from 'ngx-toastr';
import { AdminEquipmentService, EquipmentModelService } from '@app/api/api';
import { environment } from 'environments/environment';
import { ModalEditUserEquipmentComponent } from '../modal-edit-user-equipment/modal-edit-user-equipment.component';

@Component({
  selector: 'meu-modal-view-user-equipment',
  templateUrl: './modal-view-user-equipment.component.html',
  styleUrls: ['./modal-view-user-equipment.component.scss'],
})
export class ModalViewUserEquipmentComponent implements OnInit {
  userId: string = '';
  equipment: any = null;
  host: string = '';
  prefixCode: string = '';

  detailTab: 'info' | 'orders' = 'info';
  lstOrders: any[] = [];
  totalOrders: number = 0;
  orderPageSize = 10;
  orderPageIndex = 1;
  loadingDetail = false;
  loadingOrders = false;

  constructor(
    public dialogRef: MatDialogRef<ModalViewUserEquipmentComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { userId: string; equipment: any },
    private adminEquipmentService: AdminEquipmentService,
    private equipmentModelService: EquipmentModelService,
    private toastr: ToastrService,
    private ref: ChangeDetectorRef,
    public dialog: MatDialog
  ) {}

  ngOnInit() {
    this.host = environment.backendhost;
    this.userId = this.data.userId || '';
    this.equipment = this.data.equipment || null;
    this.loadEquipmentDetail();
  }

  loadEquipmentDetail() {
    if (!this.userId || !this.equipment || !this.equipment.id) {
      return;
    }
    this.loadingDetail = true;
    this.adminEquipmentService
      .apiAdminUsersEquipmentIdGet(this.userId, this.equipment.id)
      .subscribe((res) => {
        this.loadingDetail = false;
        if (res.success) {
          this.equipment = Object.assign({}, this.equipment, res.data);
        } else if (res && res.id) {
          this.equipment = Object.assign({}, this.equipment, res);
        }
        this.loadPrefixCode();
        this.ref.detectChanges();
      });
  }

  loadPrefixCode() {
    const prefixId = this.equipment && this.equipment.equipment_model_prefix_id;
    const modelId = this.equipment && this.equipment.equipment_model_id;
    // Try from nested extend_equipment_model.prefixes first
    const model = (this.equipment && this.equipment.extend_equipment_model) || {};
    const prefixes = model.prefixes || [];
    if (prefixId && prefixes.length > 0) {
      const matched = prefixes.find((p: any) => p.id === prefixId);
      if (matched) {
        const code = matched.prefix_code || '';
        const assembly = matched.assembly_type || '';
        this.prefixCode = assembly ? `${code} - ${assembly}` : code;
        this.ref.detectChanges();
        return;
      }
    }
    // Fallback: fetch equipment model detail to get prefixes list
    if (prefixId && modelId) {
      this.equipmentModelService.apiEquipmentModelsIdGet(modelId).subscribe((mRes) => {
        const mData = mRes.success ? mRes.data : mRes;
        const mPrefixes = (mData && mData.prefixes) || [];
        const matched = mPrefixes.find((p: any) => p.id === prefixId);
        if (matched) {
          const code = matched.prefix_code || '';
          const assembly = matched.assembly_type || '';
          this.prefixCode = assembly ? `${code} - ${assembly}` : code;
        }
        this.ref.detectChanges();
      });
    }
  }

  switchTab(tab: 'info' | 'orders') {
    this.detailTab = tab;
    if (tab === 'orders' && this.lstOrders.length === 0 && !this.loadingOrders) {
      this.loadOrders();
    }
  }

  loadOrders() {
    if (!this.userId || !this.equipment || !this.equipment.id) {
      return;
    }
    this.loadingOrders = true;
    this.adminEquipmentService
      .apiAdminUsersEquipmentOrdersGet(
        this.userId,
        this.equipment.id,
        this.orderPageIndex,
        this.orderPageSize
      )
      .subscribe((res) => {
        this.loadingOrders = false;
        if (res.success) {
          this.lstOrders = res.data.collection || res.data || [];
          this.totalOrders = res.data.total || this.lstOrders.length;
        } else if (Array.isArray(res)) {
          this.lstOrders = res;
          this.totalOrders = res.length;
        }
        this.ref.detectChanges();
      });
  }

  onOrderPageChange(page: number) {
    this.orderPageIndex = page;
    this.loadOrders();
  }

  getImage(element: any): string {
    const model = element.extend_equipment_model || {};
    const img = element.default_image || model.default_image || '';
    if (!img) {
      return '';
    }
    return img.indexOf('http') === 0 ? img : this.host + img;
  }

  getAllImages(element: any): string[] {
    const images: string[] = [];
    const primary = this.getImage(element);
    if (primary) {
      images.push(primary);
    }
    const extras = element.extend_images || [];
    for (const item of extras) {
      const url = item.image_url || item.url || item.path || '';
      if (url) {
        images.push(url.indexOf('http') === 0 ? url : this.host + url);
      }
    }
    return images.filter((v, i, a) => a.indexOf(v) === i);
  }

  getBrandLogo(element: any): string {
    const brand = element.extend_brand || {};
    const logo = brand.logo || '';
    if (!logo) {
      return '';
    }
    return logo.indexOf('http') === 0 ? logo : this.host + logo;
  }

  getBrandName(element: any): string {
    const brand = element.extend_brand || {};
    return element.brand_name_manual || brand.name || '';
  }

  getBrandCode(element: any): string {
    const brand = element.extend_brand || {};
    return brand.code || '';
  }

  getBrandNation(element: any): string {
    const brand = element.extend_brand || {};
    return brand.nation || '';
  }

  getModelCode(element: any): string {
    const model = element.extend_equipment_model || {};
    return element.model_name_manual || model.model_code || '';
  }

  getModelName(element: any): string {
    const model = element.extend_equipment_model || {};
    return element.model_name_manual || model.name || model.model_code || '';
  }

  getUserName(element: any): string {
    const user = element.extend_user || {};
    return user.full_name || user.email || '';
  }

  getPrefixCode(element: any): string {
    return this.prefixCode || '';
  }

  getUserAvatar(element: any): string {
    const user = element.extend_user || {};
    const avatar = user.avatar || '';
    if (!avatar) {
      return '';
    }
    return avatar.indexOf('http') === 0 ? avatar : this.host + avatar;
  }

  getLocationHistory(element: any): any[] {
    return element.extend_location_history || [];
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'active':
        return 'Đang hoạt động';
      case 'inactive':
        return 'Ngừng hoạt động';
      case 'maintenance':
        return 'Bảo trì';
      case 'retired':
        return 'Ngừng sử dụng';
      default:
        return status || '';
    }
  }

  openEditModal() {
    if (!this.userId || !this.equipment || !this.equipment.id) {
      return;
    }
    const dialogRef = this.dialog.open(ModalEditUserEquipmentComponent, {
      width: '900px',
      data: {
        userId: this.userId,
        equipment: this.equipment,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.event) {
        this.toastr.success('Cập nhật thiết bị thành công');
        this.loadEquipmentDetail();
        this.dialogRef.close({ event: true });
      } else if (result && result.message) {
        this.toastr.error(result.message);
      }
    });
  }

  closeModal() {
    this.dialogRef.close();
  }
}
